import { AgGrid } from "@jsq/ds/core";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { ComplianceCaseFilters, TextFilter } from "~/pages/Compliance/ComplianceTable/types";
import { toDbId } from "~/utils/graphql-helpers";
import { FormValues } from "../CreateCaseForm/types";
import { Programs } from "./constants";

/**
 * This returns the client id from the values. There are two ways to set values, one using
 * subscription url & the other being the client and entity selectors. This differentiates
 * between the two and responds with the arena id.
 *
 * @param values The form values to get the data from
 * @returns The arena/client id or null if we can't get the value
 */
export const getClientId = (values: FormValues) => {
  return values.client?.id ? toDbId(values.client.id).toString() : null;
};

/**
 * This returns the entity id from the values. There are two ways to set values, one using
 * subscription url & the other being the client and entity selectors. This differentiates
 * between the two and responds with the entity id.
 *
 * @param values The form values to get the data from
 * @returns The entity id or null if we can't get the value
 */
export const getEntityId = (values: FormValues) => {
  return values.entity?.id ? toDbId(values.entity.id).toString() : null;
};

/**
 * Parses an integer ID from a numeric string or from the path segment after `segment`.
 * Returns null when the value is empty or not a valid ID/URL.
 *
 * @param idOrUrl The value to get the ID from
 * @param segment The URL path segment that immediately precedes the ID
 * @returns The extracted ID, or null if it couldn't be determined
 */
export const extractIdFromPathSegment = (idOrUrl: string, segment: string): number | null => {
  if (!idOrUrl) {
    return null;
  }

  // First, if it's a number, no need to do URL parsing, just grab it
  const id = parseInt(idOrUrl) || null;
  if (id) {
    return id;
  }

  try {
    const url = new URL(idOrUrl);
    let segmentSeen = false;
    for (const subdir of url.pathname.split("/")) {
      if (segmentSeen) {
        return parseInt(subdir) || null;
      }

      if (subdir === segment) {
        segmentSeen = true;
      }
    }
    return null;
  } catch {
    return null;
  }
};

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null | undefined,
): ComplianceCaseFilters[] => {
  const queryFilters: ComplianceCaseFilters[] = [];
  if (!filterModel) {
    return queryFilters;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter;
    // for now, we're not allowing multiple filters for field, so we won't hit this case
    if (filterInfo.conditions) {
      if (filterInfo.operator == "OR") {
        queryFilters.push({
          field: field,
          filterValues: filterInfo.conditions.map((condition: TextFilter) => condition.filter),
        });
      } else if (filterInfo.operator == "AND") {
        filterInfo.conditions.forEach((condition: TextFilter) => {
          queryFilters.push({ field: field, filterValues: [condition.filter] });
        });
      } else {
        // Shouldn't hit this error
        throw Error("Unexpected operator for multiple conditions");
      }
    } else if (filterInfo.filterType == "set") {
      queryFilters.push({ field: field, filterValues: filterInfo.values });
    } else if (filterInfo.filterType == "text") {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter] });
    } else {
      // Would need to change this if we add other filter types
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters;
};

/**
 * Determines whether the risk rating should be displayed for a given program.
 *
 * @param programId - The ID of the program to check. Can be undefined if no program is selected.
 * @returns True if the risk rating should be shown, false otherwise.
 */
export const shouldShowRiskRating = (programId: number | undefined): boolean => {
  if (!programId) return false;
  return (
    programId === Programs.US_AML ||
    programId === Programs.LUXEMBOURG_AML ||
    programId === Programs.CAYMANS_AML
  );
};

/**
 * Determines whether the case type is a standalone case type.
 *
 * Profile-level cases are associated with a profile and do not require
 * a specific External Arena, Entity, or Program. Opposed to
 * position-level cases which are tied to a specific position and require
 * these external references.
 * Examples: profile_refresh, document_refresh, CiC.
 *
 * @param caseType - The case type to check. Can be undefined if no case type is selected.
 * @returns True if the case type is standalone, false otherwise.
 */
export const isStandaloneCaseType = (caseType: ComplianceCaseTypeEnum | null | undefined) => {
  if (!caseType) return false;
  return (
    caseType === ComplianceCaseTypeEnum.ProfileRefresh ||
    caseType === ComplianceCaseTypeEnum.ChangeInCircumstance ||
    caseType === ComplianceCaseTypeEnum.DocumentRefresh
  );
};

/**
 * Determines whether the case type is an investor onboarding case or a
 * standalone case type. Used to gate features (e.g. tax detail capture) that
 * apply to both onboarding and profile-level cases.
 */
export const isFatcaCaseType = (caseType: ComplianceCaseTypeEnum | null | undefined) => {
  return (
    caseType === ComplianceCaseTypeEnum.InvestorOnboarding ||
    caseType === ComplianceCaseTypeEnum.Remediation ||
    isStandaloneCaseType(caseType)
  );
};
